import { PlusIcon } from "@xola/icons";
import { flash, SubmitButton } from "@xola/ui-kit";
import { FormField, InputField, requiredString } from "common";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { Seller, TelemetryModule, TelemetryModuleConfiguration, TelemetryModuleParameter } from "types";
import { TelemetryModuleParameterValue } from "types/src/types/Telemetry";
import { object, string } from "yup";
import { api } from "../../api/api";
import { useSeller } from "../../hooks/useAccount";
import { useInvalidate } from "../../hooks/useInvalidation";
import { useVisibility } from "../../hooks/useVisibility";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { ContentLayout } from "../Layout/ContentLayout";
import { TelemetryModuleParameterModal } from "./TelemetryModuleParameterModal";
import { TelemetryParameterItem } from "./TelemetryParameterItem";

interface EditTelemetryModuleFormProps {
    telemetryModule?: TelemetryModule;
    configuration?: TelemetryModuleConfiguration;
}

const validationSchema = object().shape({
    seller: object().shape({
        id: string(),
    }),
    description: string(),
    name: requiredString(),
});

const initialValues = (seller?: Seller, module?: TelemetryModule, configuration?: TelemetryModuleConfiguration) => {
    if (!module) {
        return {
            id: "",
            name: "",
            description: "",
            parameters: [],
            seller,
        };
    }

    return {
        id: module.id,
        seller: module.seller ?? undefined,
        name: module.name,
        description: module.description,
        parameters: getParams(module, configuration),
    };
};

const getParams = (module?: TelemetryModule, configuration?: TelemetryModuleConfiguration) => {
    if (!module) {
        return [];
    }

    if (!configuration) {
        return module.parameters;
    }

    return module.parameters.map((parameter: TelemetryModuleParameter) => {
        const configParam = configuration.parameters.find(
            (configParam: TelemetryModuleParameterValue) => configParam.key === parameter.key,
        );
        parameter.value = configParam?.value;
        return parameter;
    });
};

const getConfiguration = (seller: Seller, module: TelemetryModule) => {
    const defaultConfiguration = {
        enabled: false,
        module: {
            id: module.id ?? "",
        },
        parameters: [],
    };
    if (!seller.preferences.telemetry) {
        return defaultConfiguration;
    }

    const configuration = seller.preferences.telemetry.modules?.find((config) => config.module.id === module.id);
    return configuration ?? defaultConfiguration;
};

export const EditTelemetryModuleForm = ({ telemetryModule, configuration }: EditTelemetryModuleFormProps) => {
    const seller = useSeller();
    const history = useHistory();
    const invalidate = useInvalidate();
    const data = initialValues(seller, telemetryModule, configuration);
    const [showModal, { toggle: toggleShowModal }] = useVisibility(false);
    const [activeParameter, setActiveParameter] = useState<TelemetryModuleParameter | null>(null);

    const { mutateAsync: saveModule, isLoading: isSavingModule } = useMutation(async (values: TelemetryModule) => {
        const url = "/telemetryModules";
        const { data: savedModule } = await (values.id
            ? api.put<TelemetryModule>(`${url}/${values.id}`, values)
            : api.post<TelemetryModule>(url, values));
        invalidate("telemetryModules", values.id);
        return savedModule;
    });

    const { mutateAsync: saveConfiguration, isLoading: isSavingConfiguration } = useMutation(
        async (values: TelemetryModuleConfiguration) => {
            const url = `/sellers/${seller.id}/preferences/telemetry/configurations`;
            await (values.id ? api.put(`${url}/${values.id}`, values) : api.post(url, values));
            flash.success("Successfully saved integration configuration");
            invalidate("sellers", seller.id);
        },
    );

    const handleSubmit = async (values: TelemetryModule) => {
        const parameterValues = values.parameters.map((parameter: TelemetryModuleParameter) => ({
            key: parameter.key,
            value: parameter.value,
        }));

        if (values.seller) {
            const savedModule = await saveModule(values);
            values.id = savedModule.id;

            history.push(`/marketing/serverSideTracking/modules/${savedModule.id}/edit`);
            return;
        }

        if (values.id) {
            configuration = configuration ?? getConfiguration(seller, values);
            configuration.parameters = parameterValues;
            await saveConfiguration(configuration);
        }
    };

    const handleReset = () => {
        history.push("/marketing/serverSideTracking");
    };

    const handleSaveParameter = (parameter: TelemetryModuleParameter, setFieldValue: any, values: TelemetryModule) => {
        let newParameters = [...values.parameters, parameter];
        if (parameter.id) {
            newParameters = values.parameters.map((existingParameter: TelemetryModuleParameter) =>
                existingParameter.id === parameter.id ? parameter : existingParameter,
            );
        }

        setFieldValue("parameters", newParameters);
    };

    const handleDeleteParameter = (
        parameter: TelemetryModuleParameter,
        setFieldValue: any,
        values: TelemetryModule,
    ) => {
        const newParameters = values.parameters.filter(
            (existingParameter: TelemetryModuleParameter) => existingParameter.key !== parameter.key,
        );
        setFieldValue("parameters", newParameters);
    };

    const handleEditParameter = (parameter: TelemetryModuleParameter) => {
        setActiveParameter(parameter);
        toggleShowModal();
    };

    return (
        <XolaFormik
            enableReinitialize
            validateOnChange
            validateOnBlur={false}
            validationSchema={validationSchema}
            initialValues={data}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, values, setFieldValue }: any) => (
                <ContentLayout isWide className="space-y-6" isDisabled={isSavingModule || isSavingConfiguration}>
                    <ContentLayout.Section
                        title="Integration Details"
                        description="Basic information about this integration"
                    >
                        <div className="space-y-4">
                            <FormField name="name" label="Name">
                                <InputField isRequired value={values.name} disabled={!!values.seller} />
                            </FormField>

                            <FormField name="description" label="Description">
                                <InputField value={values.description} disabled={values.seller ? undefined : true} />
                            </FormField>
                        </div>
                    </ContentLayout.Section>

                    <hr className="my-2 text-gray-light" />

                    <ContentLayout.Section
                        title="Parameters"
                        description="These parameters will be collected from the customer's browser during checkout"
                    >
                        {values.seller ? (
                            <div className="items-left my-2 flex grow">
                                <SecondaryButton size="small" icon={<PlusIcon />} onClick={toggleShowModal}>
                                    Add Parameter
                                </SecondaryButton>
                            </div>
                        ) : null}
                        <div className="space-y-4">
                            {values.parameters.map((parameter: TelemetryModuleParameter, index: number) => (
                                <TelemetryParameterItem
                                    key={parameter.key}
                                    index={index}
                                    parameter={parameter}
                                    module={values}
                                    onEdit={handleEditParameter}
                                    onDelete={(parameter) => {
                                        handleDeleteParameter(parameter, setFieldValue, values);
                                    }}
                                />
                            ))}
                        </div>
                    </ContentLayout.Section>

                    <ContentLayout.Footer>
                        <SubmitButton isLoading={isSavingModule || isSavingConfiguration} onClick={handleSubmit}>
                            Save
                        </SubmitButton>

                        <SecondaryButton type="reset" onClick={handleReset}>
                            Cancel
                        </SecondaryButton>
                    </ContentLayout.Footer>

                    <TelemetryModuleParameterModal
                        isOpen={showModal}
                        parameter={activeParameter}
                        onClose={toggleShowModal}
                        onApply={(parameter: TelemetryModuleParameter) =>
                            handleSaveParameter(parameter, setFieldValue, values)
                        }
                    />
                </ContentLayout>
            )}
        </XolaFormik>
    );
};
